import { currentLoggedRelyingParty } from '@/shared/api';
import { ROLLOUT_GROUP, type RolloutGroupResponse } from '@/shared/api/graphql/account';
import { CANARY, toRolloutGroup } from '@/shared/lib/constants/rolloutGroup';
import {
  consumeEnteredApp,
  dismissPrompt,
  isPinnedToStable,
  SERVED_ROLLOUT_GROUP,
  setRolloutGroupCookie,
  wasPromptDismissed
} from '@/shared/lib/util/rolloutGroupCookie';
import { useQuery, useReactiveVar } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Forces the document to be re-fetched through the load balancer so the canary target group
 * actually serves us.
 *
 * `window.location.reload()` already navigates with the "reload" cache mode, so against our own
 * origin -- where nginx sends `no-store` on index.html anyway -- this adds little. What it does
 * add, and why the canary direction uses it: a fresh query string changes the cache key for any
 * proxy that ignores `no-store`, and `replace` drops the history entry, so Back cannot restore a
 * bfcached document of the *other* build alongside a cookie that now disagrees with it.
 *
 * The value stays digits-only on purpose: the helpers in `util/location.ts` substring-match
 * `window.location.search` when deciding which side-nav item is active, so a value containing a
 * route fragment would highlight the wrong one. `public/bootstrap.js` strips the param before the
 * router ever evaluates.
 */
const hardRefresh = () => {
  const url = new URL(window.location.href);
  url.searchParams.set('rg', String(Date.now()));
  window.location.replace(url.toString());
};

export const useRolloutGroupSync = () => {
  const currentOrganization = useReactiveVar(currentLoggedRelyingParty);
  const relyingPartyId = currentOrganization?.id;

  const { data, error, loading } = useQuery<RolloutGroupResponse>(ROLLOUT_GROUP, {
    variables: { relyingPartyId },
    skip: !relyingPartyId,
    // Never served from cache: the whole point is to notice an enrolment or a rollback that
    // happened since this browser last asked.
    fetchPolicy: 'network-only'
  });

  // Anything unexpected -- a group we do not recognise, a null, or a failed query during a
  // backend/web deploy window -- resolves to the stable build.
  const targetGroup = toRolloutGroup(error ? undefined : data?.relyingParty?.rolloutGroup);

  const [dismissedAt, setDismissedAt] = useState<string>();

  // Whether this mount was reached by logging in or switching company. Read once, synchronously,
  // on the first render -- not inside the effect below -- because the prompt is decided during
  // render. Consuming it in the effect meant the dialog rendered and painted for one frame before
  // the effect navigated away, which is the flash of prompt in the middle of an auto-reload.
  const entryRef = useRef<boolean | null>(null);
  if (entryRef.current === null) entryRef.current = consumeEnteredApp();

  // Latches once the reload is issued, so a re-render between calling it and the browser actually
  // navigating cannot slip the prompt back on screen.
  const reloadingRef = useRef(false);

  // Compared against the group captured at page load, not a live cookie read. The cookie is not
  // reactive, so comparing against it meant the effect below erased its own trigger: the prompt
  // would vanish on the next unrelated re-render without the user having chosen anything.
  const mismatched = !!relyingPartyId && !loading && !error && targetGroup !== SERVED_ROLLOUT_GROUP;

  const reload = useCallback(() => {
    setRolloutGroupCookie(targetGroup);
    if (targetGroup === CANARY) hardRefresh();
    else window.location.reload();
  }, [targetGroup]);

  // True on the render that is about to navigate, so the prompt is never shown on it.
  const autoReloading = reloadingRef.current || (mismatched && !!entryRef.current && !isPinnedToStable());

  // Keep the routing cookie aligned quietly in the background. Harmless on its own -- it changes
  // nothing until the next document request.
  //
  // Then: if this mount is a login or a company switch, reload without asking. Those are the only
  // two moments where nobody can be mid-task, so there is no work to interrupt. Any other time --
  // a mismatch appearing while someone is using the app -- only syncs the cookie and offers the
  // prompt, because reloading someone out of a half-filled form is what this exists to avoid.
  useEffect(() => {
    if (loading || !relyingPartyId) return;

    if (mismatched && !isPinnedToStable()) {
      setRolloutGroupCookie(targetGroup);

      if (entryRef.current) {
        // Cleared before navigating, so a reload that does not actually change the served build
        // (no load balancer locally, or a misconfigured rule) reloads once rather than forever.
        entryRef.current = false;
        reloadingRef.current = true;
        reload();
        return;
      }
    }

    // First settled result of this mount, mismatch or not: the entry window is over. Without this
    // a session that started in sync would still count as "entering" hours later, and a mismatch
    // appearing mid-work would reload someone out of what they were doing.
    entryRef.current = false;
  }, [loading, relyingPartyId, mismatched, targetGroup, reload]);

  const dismiss = () => {
    if (!relyingPartyId) return;
    dismissPrompt(relyingPartyId, targetGroup);
    // Mirrors what sessionStorage now holds, purely so this render pass updates; the stored value
    // stays the source of truth. Comparing the exact pair (rather than a boolean) keeps a
    // dismissal from carrying over to a different pharmacy or the opposite direction.
    setDismissedAt(`${relyingPartyId}:${targetGroup}`);
  };

  const dismissed =
    !!relyingPartyId && (wasPromptDismissed(relyingPartyId, targetGroup) || dismissedAt === `${relyingPartyId}:${targetGroup}`);

  const showPrompt = mismatched && !autoReloading && !dismissed && !isPinnedToStable();

  return { showPrompt, targetGroup, reload, dismiss };
};
